import { createFileRoute, Link } from "@tanstack/react-router";
import { EmptyState, PageHeader } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { formatBytes } from "@/lib/bytes";
import { timeAgo } from "@/lib/format";
import { useDeviceNames, useVault } from "@/lib/hooks";

export const Route = createFileRoute("/_app/files/")({ component: FilesPage });

function FilesPage() {
  const v = useVault();
  const deviceName = useDeviceNames();
  const projects = new Map((v.snapshot?.projects ?? []).map((p) => [p.id, p.name]));
  const files = (v.snapshot?.files ?? [])
    .filter((f) => f.kind === "blob")
    .sort((a, b) => (b.last_change?.wall_ms ?? 0) - (a.last_change?.wall_ms ?? 0));
  return (
    <>
      <PageHeader
        title="Files"
        description="Whole-file backups and synced non-env files, with every version kept. Restore to disk with `envstash restore <path> [@version]`."
      />
      {files.length === 0 ? (
        <EmptyState title="No backed-up files yet">
          Dotfiles like ~/.zshrc show up here once the daemon has run on a Mac.
        </EmptyState>
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {files.map((f) => (
            <li key={f.id}>
              <Link
                to="/files/$fileId"
                params={{ fileId: f.id }}
                className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-accent/40"
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm">
                    <span className="text-muted-foreground">{projects.get(f.project_id) ?? "?"}/</span>
                    {f.rel_path}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {f.blob_history.length} version{f.blob_history.length === 1 ? "" : "s"}
                    {f.blob_current
                      ? ` · ${formatBytes(f.blob_current.size)} · ${deviceName(f.blob_current.hlc.device_id)} · ${timeAgo(f.blob_current.hlc.wall_ms)}`
                      : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {f.blob_conflicts.length > 0 ? <Badge variant="destructive">conflict</Badge> : null}
                  <Badge variant="outline">{f.mode}</Badge>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
